import diagramApplication from './diagram-application.png'

const content = `<p>The term “Factory” has some confusion around it because several design concepts contain this term. There are two design patterns, “Factory method” and “Abstract factory”, plus something that people tend to just call “Factory”.</p>

<p>Here we look at the simple “Factory”.</p>

<p>Factory are classes or just functions that encapsulate the logic of construction of an object. Often, this involves choosing the type of an object. They help increase cohesion and decrease coupling in the code.</p>

<p>Let’s take an example to illustrate. A diagram drawing application has a command palette where you can choose various shapes (rectangles, arrows, lines, text) to add to the drawing:</p>

<img src=${diagramApplication} alt="diagram-application" />

<p>When the user clicks on one of the shapes in the palette, it creates that a shape in the middle of the canvas.</p>

<p>One way to design this action is to have a <code>Canvas</code> class that represents the current diagram. The <code>Canvas</code> contains instances of specific classes that represent the shapes: <code>Rectangle</code>, <code>Arrow</code>, <code>Line</code> and <code>Text</code>.</p>

<p>How do we design the interface of <code>Canvas</code> to add a new shape?</p>

<p>One way is to pass the class to instantiate:</p>

<pre><code class="language-ruby">class Canvas
  def addShape(klass, x, y)
    @shapes &lt;&lt; klass.new(x, y)
  end
end</code></pre>

<p>The palette can then call this code:</p>

<pre><code class="language-ruby"># Code of the Rectangle button in the palette

canvas.addShape(Rectangle, 0, 0)</code></pre>

<p>This makes for a light code in the <code>Canvas</code>, but an issue with this design is that the palette knows about the classes that the canvas uses. This is a form of <a href="/low-coupling">coupling</a> between the palette and the canvas.</p>

<p>Indeed, if we change the code of the canvas, for example by having the same class to represent arrows and lines, this would change the code of the palette, even if the palette keeps the exact same features. This is coupling.</p>

<p>Another way is to have in <code>Canvas</code> a method for each type of shape:</p>

<pre><code class="language-ruby">class Canvas
  def addRectangle(x, y)
    @shapes &lt;&lt; Rectangle.new(x, y)
  end
  
  def addArrow(x, y)
    @shapes &lt;&lt; Arrow.new(x, y)
  end
  
  def addLine(x, y)
    @shapes &lt;&lt; Line.new(x, y)
  end
  
  def addText(x, y)
    @shapes &lt;&lt; Text.new(x, y)
  end
end</code></pre>

<p>This hides the shapes classes from the palette, but it takes a lot of space in the <code>Canvas</code> class, and reduces its <a href="/high-cohesion">cohesion</a>. Indeed, in a drawing application, a <code>Canvas</code> class is likely to already handle a lot of concerns. Moreover, drawing applications have dozens or even hundreds of shapes, which would make this design create as many methods in the <code>Canvas</code> class.</p>

<h2 id="Introducing-a-Factory">Introducing a Factory</h2>

<p>Another option is to use strings (or an <code>enum</code> for languages supporting <code>enum</code>s) such as <code>"rectangle"</code>, <code>"arrow"</code>, <code>"line"</code>, <code>"text"</code> to represent the type of shape in the in palette.</p>

<p>The <code>Canvas</code> class has a function that takes that string:</p>

<pre><code class="language-ruby">class Canvas
  def addShape(shape_type, x, y)
    case shape_type
    when 'rectangle' then @shapes &lt;&lt; Rectangle.new(x, y)
    when 'arrow' then @shapes &lt;&lt; Arrow.new(x, y)
    when 'line' then @shapes &lt;&lt; Line.new(x, y)
    when 'text' then @shapes &lt;&lt; Text.new(x, y)
    end
  end
end</code></pre>

<p>The palette calls this function:</p>

<pre><code class="language-ruby"># Code of the Rectangle button in the palette

canvas.addShape('rectangle', 0, 0)</code></pre>

<p>This solves the problem of coupling between the classes used in the canvas and the palette, and requires only one function in <code>Canvas</code>. Also, this allows a canvas to be created from something else than a palette, for example by loading a drawing saved on a file.</p>

<p>But this <code>addShape</code> function can get arbitrary long with the many types of shapes of drawing applications, and it can have some logic to choose what class represent what type of shape (for example <code>Line</code> for both <code>"arrow"</code> and <code>"line"</code>).</p>

<p>To offload <code>Canvas</code> from all this creation concern, we can create a separate class dedicated to the creation code. This is called a factory:</p>

<pre><code class="language-ruby">class ShapeFactory
  def self.create(shape_type, x, y)
    case shape_type
    when 'rectangle' then Rectangle.new(x, y)
    when 'arrow' then Arrow.new(x, y)
    when 'line' then Line.new(x, y)
    when 'text' then Text.new(x, y)
    end
  end
end</code></pre>

<p>(it could also be a simple function instead of a class)</p>

<p>The <code>Canvas</code> class uses this factory:</p>

<pre><code class="language-ruby">class Canvas
  def addShape(shape_type, x, y)
    @shapes &lt;&lt; ShapeFactory.create(shape_type, x, y)
  end
  
  # other concerns of Canvas...
end</code></pre>

<p>And the palette still uses the <code>Canvas</code> this way:</p>

<pre><code class="language-ruby"># Code of the Rectangle button in the palette

canvas.addShape('rectangle', 0, 0)</code></pre>

<p>The factory has increased the <a href="/high-cohesion">cohesion</a> of the code by focusing on object construction and letting the rest of the code focus on something else. It is a <a href="/pure-fabrication">Pure fabrication</a>.</p>

<p>In our example the factory also helped decouple the palette from the canvas and prevent the <code>Canvas</code> from growing arbitrarily with the number of shapes.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Factories encapsulate the logic of creating objects</li><li>They relieve the rest of the code from that logic, increasing cohesion and reducing coupling</li></ul>
</div>`

export default content
